import { Avatar, Button, Col, Flex, Grid, Image, Row } from "antd";
import React, { useState } from "react";

import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import EmptyCoach from "../../assets/images/content/empty_coach.png";
import MapPinFilled from "../../assets/images/icons/MapPinFilled";
import Ski from "../../assets/images/icons/Ski";
import { H3, H5, H6, P1, P2 } from "../../components/NormalFonts";
import { useGetCoaches, useGetCoachInfo } from "../../hooks/Coach/coach.hooks";
import {
  CoachInfoResponse,
  CoachResponse,
  DataMappingType,
} from "../../hooks/Coach/data";
import { ContentWrapper } from "../../layouts/components";
import { pushGtag, scrollToTop } from "../../plugins/utils";
import { BookModal } from "./components";
import { CoachInfoProps } from "./data";
import { assign } from "./enumerations/assign";
import styles from "./styles.module.scss";

const CoachInfo: React.FC<CoachInfoProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const pathname = window.location.pathname.split("/");
  const coachId = [...pathname].pop();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const PROFILE_ITEMS: DataMappingType[] = [
    {
      key: "ski_resort_codes",
      icon: (
        <MapPinFilled
          style={{ fontSize: screens.md ? 28 : 14, color: "#06304D" }}
        />
      ),
      render: (record: CoachInfoResponse) => {
        return record.ski_resort_codes.length <= 0 ? (
          <H6
            className={styles.contentDesc}
            weight="normal"
            color="var(--primary-90)"
          >
            {t("normal.empty")}
          </H6>
        ) : (
          <H6
            className={styles.contentDesc}
            weight="normal"
            color="var(--primary-90)"
          >
            {record.ski_resort_codes
              .map((code: string) => {
                return t(`skiresort.${code}`);
              })
              .join(" / ")}
          </H6>
        );
      },
    },
    {
      key: "skill_codes",
      icon: (
        <Ski style={{ fontSize: screens.md ? 28 : 14, color: "#06304D" }} />
      ),
      render: (record: CoachInfoResponse) => {
        return record.skill_codes.length <= 0 ? (
          <H6
            className={styles.contentDesc}
            weight="normal"
            color="var(--primary-90)"
          >
            {t("normal.empty")}
          </H6>
        ) : (
          <H6
            className={styles.contentDesc}
            weight="normal"
            color="var(--primary-90)"
          >
            {record.skill_codes
              .map((skill: string) => t(`normal.skill.${skill}`))
              .join(" / ")}
          </H6>
        );
      },
    },
  ];

  const { refetch: refetchCoaches, isFetching } = useGetCoaches(
    { branch_id: "1" },
    { enabled: false }
  );
  const { data: coachInfo } = useGetCoachInfo(
    { coach_id: coachId },
    { enabled: !!coachId }
  );

  const renderItem = (
    _item: DataMappingType,
    profile: CoachInfoResponse | undefined
  ) => {
    if (!profile) return;
    if (_item.render) return _item.render(profile);
    return profile[_item.key as keyof CoachInfoResponse] as React.ReactNode;
  };

  const handleClickNext = () => {
    refetchCoaches().then((res) => {
      switch (res.status) {
        case "success":
          console.debug(res.data);
          const nowCoach = res.data.findIndex(
            (coach: CoachResponse) => coach.account_id === Number(coachId)
          );
          if (nowCoach === res.data.length - 1) return;
          navigate(`/trainers/info/${res.data[nowCoach + 1].account_id}`);
          scrollToTop("preview-container");
          break;
        case "error":
          console.debug(res.error);
          break;
        default:
          break;
      }
    });
  };

  const handleClickBook = () => {
    pushGtag({
      event: "book_coach",
      info: coachInfo,
    });

    setIsModalOpen(true);
  };

  return (
    <ContentWrapper>
      <div
        className={`${styles.previewContainer} ${
          screens.md ? "" : styles.mobile
        }`}
        id="preview-container"
      >
        <div className={styles.pictureContainer}>
          <H5
            weight="heavy"
            color="var(--primary)"
            className={styles.mobileTitle}
          >
            {coachInfo?.nickname}
          </H5>
          <Avatar
            size={253}
            className={`${styles.avatar} ${screens.xl ? "" : styles.small}`}
            shape={"square"}
            src={coachInfo?.profile_url}
            icon={
              <Avatar
                size={253}
                className={`${styles.avatar} ${screens.xl ? "" : styles.small}`}
                shape={"square"}
                src={EmptyCoach}
              />
            }
          />
          <div className={styles.bioContainer}>
            <H3
              weight="heavy"
              color="var(--primary)"
              className={styles.pcTitle}
            >
              {coachInfo?.nickname}
            </H3>
            <div className={styles.profiles}>
              {PROFILE_ITEMS.map((_profile: DataMappingType) => {
                return (
                  <div key={_profile.key}>
                    <div className={styles.profileItem}>
                      {_profile.icon}
                      {renderItem(_profile, coachInfo)}
                    </div>
                  </div>
                );
              })}
            </div>
            {coachInfo?.can_assign === assign.ALLOW && (
              <Button
                className={styles.pcButton}
                size="middle"
                style={{
                  background: "var(--primary)",
                  color: "var(--Neutral-light-10)",
                }}
                variant="solid"
                onClick={handleClickBook}
              >
                {t("preview.book")}
              </Button>
            )}
          </div>
        </div>
        <P1 color="#272727" style={{ marginBottom: screens.md ? 60 : 40 }}>
          <div
            style={{
              textWrap: "wrap",
              fontSize: screens.md ? 16 : 14,
              whiteSpace: "pre-wrap",
            }}
          >
            {coachInfo?.bio}
          </div>
        </P1>
        {coachInfo?.teaching_media?.length > 0 ? (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("profile.photo")}
            </H5>
            <Image.PreviewGroup>
              <Row
                gutter={[24, 24]}
                style={{ marginBottom: screens.md ? 60 : 40 }}
              >
                {coachInfo?.teaching_media?.map(
                  (photo: string, index: number) => {
                    return (
                      <Col span={screens.xs ? 24 : 12} key={`photos_${index}`}>
                        <div className={styles.imageWrapper}>
                          <Image
                            src={photo}
                            width={"100%"}
                            height={"100%"}
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      </Col>
                    );
                  }
                )}
              </Row>
            </Image.PreviewGroup>
          </>
        ) : null}
        {coachInfo?.youtube_url ? (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("preview.youtube")}
            </H5>
            <div className={styles.playerContainer}>
              <ReactPlayer
                url={coachInfo?.youtube_url}
                playing={false}
                controls={true}
                width={"100%"}
                height={"100%"}
              />
            </div>
          </>
        ) : null}
        {coachInfo?.certifications?.length > 0 ? (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("preview.certificates")}
            </H5>
            <Row
              gutter={[15, 12]}
              style={{ paddingBottom: screens.lg ? 100 : 44 }}
            >
              {coachInfo?.certifications?.map(
                (certificate: string, index: number) => {
                  return (
                    <Col span={screens.xs ? 12 : 6} key={`cert_${index}`}>
                      <Image src={certificate} preview={false} />
                    </Col>
                  );
                }
              )}
            </Row>
          </>
        ) : null}
        <Flex
          align="center"
          justify="space-between"
          className={styles.actionContainer}
        >
          <Button onClick={() => navigate("/trainers")} type="link">
            <P2 color="var(--primary-60)">
              <Flex align="center" gap={4}>
                <LeftOutlined />
                {t("coachInfo.back")}
              </Flex>
            </P2>
          </Button>
          {coachInfo?.can_assign === assign.ALLOW && (
            <Button
              className={styles.pcButton}
              size="middle"
              style={{
                background: "var(--primary)",
                color: "var(--Neutral-light-10)",
              }}
              onClick={handleClickBook}
              variant="solid"
            >
              {t("preview.book")}
            </Button>
          )}
          <Button onClick={handleClickNext} type="link" loading={isFetching}>
            <P2 color="var(--primary-60)">
              <Flex align="center" gap={4}>
                {t("coachInfo.next")}
                <RightOutlined />
              </Flex>
            </P2>
          </Button>
        </Flex>
        {coachInfo?.can_assign === assign.ALLOW && (
          <div className={styles.mobileButtonContainer}>
            <Button
              className={styles.mobileButton}
              type={"primary"}
              size="small"
              block
              style={{
                background: "var(--primary)",
                color: "var(--Neutral-light-10)",
              }}
              onClick={handleClickBook}
            >
              {t("preview.book")}
            </Button>
          </div>
        )}
      </div>
      <BookModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        coachInfo={coachInfo}
      />
    </ContentWrapper>
  );
};

export default CoachInfo;
